/**
 * Canonical driver category slugs (people) — distinct from fleet asset types (bike/car/cargo).
 * Stored on driver_profiles.type; exposed as profile.type / our-drivers vehicle_type.
 */

import type { AssetType } from '@/config/fleetConstants'

export type DriverCategorySlug = '2_wheel_ev' | '4_wheel_ev' | 'cargo_ev' | 'contractor'

export const DRIVER_CATEGORY_SLUGS: readonly DriverCategorySlug[] = [
  '2_wheel_ev',
  '4_wheel_ev',
  'cargo_ev',
  'contractor',
] as const

export interface DriverCategoryConfig {
  slug: DriverCategorySlug
  label: string
  /** Fleet asset type when this category maps to a vehicle class. */
  assetType: AssetType | null
}

export const DRIVER_CATEGORIES: DriverCategoryConfig[] = [
  { slug: '2_wheel_ev', label: '2-Wheel EV', assetType: 'bike' },
  { slug: '4_wheel_ev', label: '4-Wheel EV', assetType: 'car' },
  { slug: 'cargo_ev', label: 'Cargo EV', assetType: 'cargo' },
  { slug: 'contractor', label: 'Contractor', assetType: null },
]

export const DRIVER_CATEGORY_BY_SLUG = Object.fromEntries(
  DRIVER_CATEGORIES.map((c) => [c.slug, c]),
) as Record<DriverCategorySlug, DriverCategoryConfig>

/** Select options for forms that write driver category (value = slug). */
export const DRIVER_CATEGORY_SELECT_OPTIONS = DRIVER_CATEGORIES.map((c) => ({
  value: c.slug,
  text: c.label,
}))

/** Exact alias → slug (lowercase keys). */
const EXACT_ALIASES: Record<string, DriverCategorySlug> = {
  '2_wheel_ev': '2_wheel_ev',
  '4_wheel_ev': '4_wheel_ev',
  cargo_ev: 'cargo_ev',
  contractor: 'contractor',
  '2wheeler': '2_wheel_ev',
  '4wheeler': '4_wheel_ev',
  '2-wheel ev': '2_wheel_ev',
  '4-wheel ev': '4_wheel_ev',
  'cargo ev': 'cargo_ev',
  '2-wheel ev (electric motorbike)': '2_wheel_ev',
  'electric motorbike': '2_wheel_ev',
  '4 wheel electric car': '4_wheel_ev',
  'cargo electric vehicle': 'cargo_ev',
  other: 'cargo_ev',
}

/**
 * Normalize any legacy / display / slug value to a canonical driver category slug.
 * Returns null for empty or unmappable input (`other` maps to cargo_ev).
 */
export function normalizeDriverCategory(raw: string | null | undefined): DriverCategorySlug | null {
  if (raw == null) return null
  const trimmed = String(raw).trim()
  if (!trimmed) return null

  const lower = trimmed.toLowerCase()
  if (EXACT_ALIASES[lower]) return EXACT_ALIASES[lower]

  if (
    lower.includes('contractor')
  ) {
    return 'contractor'
  }
  if (lower.includes('cargo')) {
    return 'cargo_ev'
  }
  if (
    lower.includes('motorbike') ||
    lower.includes('motorcycle') ||
    lower.includes('2 wheel') ||
    lower.includes('2-wheel') ||
    lower.includes('2wheeler') ||
    lower === 'bike'
  ) {
    return '2_wheel_ev'
  }
  if (
    lower.includes('4 wheel') ||
    lower.includes('4-wheel') ||
    lower.includes('4wheeler') ||
    lower.includes('electric car') ||
    lower === 'car'
  ) {
    return '4_wheel_ev'
  }

  return null
}

export function driverCategoryLabel(raw: string | null | undefined): string {
  const slug = normalizeDriverCategory(raw)
  if (!slug) return raw?.trim() || 'N/A'
  return DRIVER_CATEGORY_BY_SLUG[slug].label
}

export function toAssetType(raw: string | null | undefined): AssetType | null {
  const slug = normalizeDriverCategory(raw)
  if (!slug) return null
  return DRIVER_CATEGORY_BY_SLUG[slug].assetType
}

/** @deprecated Prefer normalizeDriverCategory — kept as explicit alias name from plan. */
export function fromLegacyVehicleType(raw: string | null | undefined): DriverCategorySlug | null {
  return normalizeDriverCategory(raw)
}

export function isDriverCategorySlug(value: string | null | undefined): value is DriverCategorySlug {
  return !!value && (DRIVER_CATEGORY_SLUGS as readonly string[]).includes(value)
}
